import { Badge, Button, Card, Field, Icon, PageHeader, SelectField, Skeleton } from "@nelurio/ui";
import type { Entitlement } from "@nelurio/contracts";
import { useAuth } from "../auth/AuthProvider";
import { languages, useI18n } from "@nelurio/i18n";
import { api, ApiClientError, isDemoMode, API_BASE_URL } from "../api/client";
import { useNavigate } from "react-router";
import React, { useEffect, useState } from "react";
import { ACCENTS, applyAccent, readAccent, storeAccent, type AccentKey } from "../theme/accents";
import { disableWebPush, enableWebPush, pushConnection, type PushConnection } from "../lib/webPush";
import { defaultLayout, loadLayout, notifyLayoutChanged, saveLayout } from "../widgets/registry";

type SettingsTab = "profile" | "appearance" | "subscription" | "language" | "notifications" | "support" | "privacy";

interface ProfileData {
  displayName: string;
  email: string;
  nativeLanguage?: string | null;
  studyLanguage?: string | null;
  timezone: string;
  university?: string | null;
  program?: string | null;
  authProvider: string;
  createdAt?: string;
}

const timezones = ["Europe/Berlin", "Europe/Vienna", "Europe/Zurich", "Europe/London", "Europe/Kyiv", "Asia/Tehran", "Asia/Dubai", "America/New_York"];

export function SettingsPage() {
  const { t, locale, setLocale } = useI18n();
  const navigate = useNavigate();
  // Deep-linkable tabs: /settings?tab=support opens the support tab directly.
  const [tab, setTab] = useState<SettingsTab>(() => {
    const requested = new URLSearchParams(window.location.search).get("tab");
    const valid: SettingsTab[] = ["profile", "appearance", "subscription", "language", "notifications", "support", "privacy"];
    return (valid as string[]).includes(requested ?? "") ? (requested as SettingsTab) : "profile";
  });
  useEffect(() => {
    const url = new URL(window.location.href);
    url.searchParams.set("tab", tab);
    window.history.replaceState(null, "", url.toString());
  }, [tab]);

  return (
    <div className="page-stack narrow-page rise">
      <PageHeader title={t("settings.title")} subtitle={t("settings.subtitle")} />
      <div className="settings-tabs" role="tablist">
        {([["profile", "settings.tabProfile", "users"], ["appearance", "settings.tabAppearance", "sparkle"], ["subscription", "settings.tabSubscription", "star"], ["language", "settings.tabLanguage", "globe"], ["notifications", "settings.tabNotifications", "bell"], ["support", "settings.tabSupport", "message"], ["privacy", "settings.tabPrivacy", "shield"]] as const).map(([id, key, icon]) => (
          <button key={id} role="tab" aria-selected={tab === id} className={tab === id ? "is-active" : ""} onClick={() => setTab(id)}>{t(key)}</button>
        ))}
      </div>

      {tab === "profile" && <ProfileCard />}
      {tab === "appearance" && <AppearanceCard />}
      {tab === "subscription" && <SubscriptionCard />}
      {tab === "language" && <LanguageCard locale={locale} setLocale={setLocale} />}
      {tab === "notifications" && (
        <>
          <PushConnectionCard />
          <PushTestCard />
        </>
      )}
      {tab === "support" && (
        <>
          <FeedbackCard />
          <Card className="settings-card support-card">
            <h2>{t("support.title")}</h2>
            <p className="settings-card__lead">{t("support.lead")}</p>
            <a className="support-card__mail" href="mailto:a.h.khosravi1383@gmail.com?subject=Nelurio%20Support">
              <Icon name="message" size={16} />
              a.h.khosravi1383@gmail.com
            </a>
            <Button
              variant="secondary"
              icon="play"
              onClick={() => window.dispatchEvent(new Event("nelurio:replay-tour"))}
            >
              {t("support.guide")}
            </Button>
          </Card>
        </>
      )}
      {tab === "privacy" && (
      <PrivacyCard
        onReplayTour={() => {
          // AppShell listens for this event — works from any page, no navigation needed.
          window.dispatchEvent(new Event("nelurio:replay-tour"));
        }}
      />
      )}
    </div>
  );
}

function ProfileCard() {
  const { t, locale, setRegion } = useI18n();
  const [profile, setProfile] = useState<ProfileData | null>(null);
  const [displayName, setDisplayName] = useState("");
  const [university, setUniversity] = useState("");
  const [program, setProgram] = useState("");
  const [nativeLanguage, setNativeLanguage] = useState("");
  const [studyLanguage, setStudyLanguage] = useState("");
  const [timezone, setTimezone] = useState("Europe/Berlin");
  const [state, setState] = useState<"idle" | "saving" | "saved" | "mismatch" | "error" | "failed">("idle");
  const [pwState, setPwState] = useState<"idle" | "saving" | "saved" | "mismatch" | "error" | "failed">("idle");
  const [avatarUrl, setAvatarUrl] = useState<string | null>(null);
  const [referral, setReferral] = useState<{ code: string; url: string } | null>(null);
  const fileRef = React.useRef<HTMLInputElement | null>(null);

  // Avatar + invite share the profile card now; the header chip listens to the
  // same event so an upload updates the topbar instantly.
  useEffect(() => {
    if (isDemoMode()) return;
    fetch(`${API_BASE_URL}/profile/avatar`, { credentials: "include" })
      .then((response) => setAvatarUrl(response.ok ? response.url : null))
      .catch(() => setAvatarUrl(null));
    api<{ code: string; url: string }>("/profile/referral").then(setReferral).catch(() => setReferral(null));
  }, []);

  const [avatarNotice, setAvatarNotice] = useState("");
  const uploadAvatar = async (file: File) => {
    if (isDemoMode()) return;
    const form = new FormData();
    form.append("file", file);
    try {
      await api("/profile/avatar", { method: "POST", body: form });
      setAvatarUrl(`${API_BASE_URL}/profile/avatar?v=${Date.now()}`);
      setAvatarNotice("");
      window.dispatchEvent(new Event("nelurio:avatar-changed"));
    } catch {
      setAvatarNotice(t("profile.avatarUploadFailed"));
    }
  };

  useEffect(() => {
    if (isDemoMode()) return;
    api<ProfileData>("/auth/profile").then((data) => {
      setProfile(data);
      setDisplayName(data.displayName ?? "");
      // Fall back to the academic-path card's saved university/major so the
      // two views always start connected.
      let fallbackUniversity = "";
      let fallbackProgram = "";
      try {
        const academic = JSON.parse(window.localStorage.getItem("nelurio.academic.v1") ?? "{}");
        fallbackUniversity = academic.university ?? "";
        fallbackProgram = academic.major ?? "";
      } catch { /* ignore */ }
      setUniversity(data.university || fallbackUniversity);
      setProgram(data.program || fallbackProgram);
      setNativeLanguage(data.nativeLanguage ?? "");
      setStudyLanguage(data.studyLanguage ?? "");
      setTimezone(data.timezone || "Europe/Berlin");
      setRegion(data.timezone || "Europe/Berlin");
    }).catch(() => setProfile(null));
  }, []);

  const save = async (event: React.FormEvent) => {
    event.preventDefault();
    if (isDemoMode()) { setState("saved"); return; }
    setState("saving");
    try {
      const updated = await api<ProfileData>("/auth/profile", {
        method: "PATCH",
        body: JSON.stringify({ displayName, university, program, nativeLanguage: nativeLanguage || "-", studyLanguage: studyLanguage || "-", timezone })
      });
      setProfile(updated);
      // Region drives the display calendar (Iran → Solar Hijri) app-wide.
      setRegion(timezone);
      setState("saved");
      window.setTimeout(() => setState("idle"), 3500);
    } catch {
      setState("error");
    }
  };

  return (
    <Card className="settings-card">
      <div className="settings-card__head">
        <h2>{t("profile.title")}</h2>
        {profile && <Badge tone={profile.authProvider === "password" ? "neutral" : "purple"}>{profile.authProvider === "password" ? t("profile.providerPassword") : profile.authProvider}</Badge>}
      </div>
      <p className="settings-card__lead">{t("profile.lead")}</p>
      <div className="profile-extras__row">
        <div className="profile-extras__avatar">
          {avatarUrl ? <img src={avatarUrl} alt="" /> : <span>{(profile?.displayName || "N").slice(0, 1).toLocaleUpperCase(locale)}</span>}
        </div>
        <div className="profile-extras__info">
          <strong>{t("profile.avatarLabel")}</strong>
          <small>{t("profile.avatarHint")}</small>
          <input ref={fileRef} type="file" accept="image/*" hidden onChange={(event) => { const file = event.target.files?.[0]; if (file) void uploadAvatar(file); }} />
          {avatarNotice && <div className="form-error" role="alert">{avatarNotice}</div>}
          <Button variant="secondary" icon="users" onClick={() => fileRef.current?.click()}>{t("profile.avatarButton")}</Button>
        </div>
      </div>
      {referral && (
        <div className="profile-extras__invite">
          <strong>{t("profile.inviteLabel")}</strong>
          <small>{t("profile.inviteHint")}</small>
          <div className="profile-extras__link">
            <code>{referral.url}</code>
            <Button variant="secondary" icon="file" onClick={() => { void navigator.clipboard?.writeText(referral.url).catch(() => undefined); }}>
              {t("profile.copy")}
            </Button>
          </div>
        </div>
      )}
      <form onSubmit={save}>
        <div className="settings-grid">
          <label className="nl-field">
            <span className="nl-field__label">{t("profile.displayName")}</span>
            <input className="nl-input" value={displayName} onChange={(event) => setDisplayName(event.target.value)} minLength={2} maxLength={80} required />
          </label>
          <label className="nl-field">
            <span className="nl-field__label">{t("auth.email")}</span>
            <input className="nl-input" value={profile?.email ?? "…"} disabled />
          </label>
          <label className="nl-field">
            <span className="nl-field__label">{t("profile.university")}</span>
            <input className="nl-input" value={university} onChange={(event) => setUniversity(event.target.value)} maxLength={120} placeholder={t("profile.universityPlaceholder")} />
          </label>
          <label className="nl-field">
            <span className="nl-field__label">{t("profile.program")}</span>
            <input className="nl-input" value={program} onChange={(event) => setProgram(event.target.value)} maxLength={120} placeholder={t("profile.programPlaceholder")} />
          </label>
          <SelectField label={t("settings.nativeLanguage")} value={nativeLanguage} onChange={(event) => setNativeLanguage(event.target.value)}>
            <option value="">{t("profile.notSet")}</option>
            {languages.map((language) => <option key={language.code} value={language.code}>{language.nativeLabel}</option>)}
          </SelectField>
          <SelectField label={t("settings.studyLanguage")} value={studyLanguage} onChange={(event) => setStudyLanguage(event.target.value)}>
            <option value="">{t("profile.notSet")}</option>
            {languages.map((language) => <option key={language.code} value={language.code}>{language.nativeLabel}</option>)}
          </SelectField>
          <SelectField label={t("settings.timezone")} value={timezone} onChange={(event) => setTimezone(event.target.value)}>
            {timezones.map((zone) => <option key={zone} value={zone}>{zone}</option>)}
          </SelectField>
        </div>
        <div className="feedback-card__footer">
          <Button type="submit" loading={state === "saving"}>{t("common.save")}</Button>
          {state === "saved" && <p className="settings-notice">{t("profile.saved")}</p>}
          {state === "error" && <p className="form-error" role="alert">{t("common.error")}</p>}
        </div>
      </form>

      <div className="account-block">
        <h2>{t("account.title")}</h2>
        <div className="account-facts">
          <div><span>{t("auth.email")}</span><strong>{profile?.email || "—"}</strong></div>
          <div><span>{t("profile.planLabel")}</span><strong>{profile?.authProvider === "password" ? t("profile.planFreeLabel") : profile?.authProvider}</strong></div>
          <div><span>{t("profile.providerLabel")}</span><strong>{profile?.authProvider === "password" ? t("profile.providerPasswordValue") : profile?.authProvider}</strong></div>
          {profile?.createdAt && <div><span>{t("profile.memberSince")}</span><strong>{new Date(profile.createdAt).toLocaleDateString(locale)}</strong></div>}
        </div>

        <h3>{t("account.changePassword")}</h3>
        <form
          className="account-password-form"
          onSubmit={async (event) => {
            event.preventDefault();
            const form = event.currentTarget;
            const current = (form.elements.namedItem("current") as HTMLInputElement).value;
            const next = (form.elements.namedItem("next") as HTMLInputElement).value;
            const confirmValue = (form.elements.namedItem("confirm") as HTMLInputElement).value;
            setPwState("saving");
            if (next !== confirmValue) { setPwState("mismatch"); return; }
            try {
              await api("/auth/password/change", { method: "POST", body: JSON.stringify({ currentPassword: current, newPassword: next }) });
              setPwState("saved");
              form.reset();
            } catch (reason) {
              // "wrong password" only for the actual 401/invalid_credentials —
              // other failures (validation, server hiccup) must not blame the password.
              setPwState(reason instanceof ApiClientError && reason.status === 401 && reason.code === "invalid_credentials" ? "error" : "failed");
            }
          }}
        >
          <label className="nl-field"><span className="nl-field__label">{t("account.currentPassword")}</span>
            <input className="nl-input" type="password" name="current" required minLength={8} autoComplete="current-password" />
          </label>
          <label className="nl-field"><span className="nl-field__label">{t("account.newPassword")}</span>
            <input className="nl-input" type="password" name="next" required minLength={8} autoComplete="new-password" />
          </label>
          <label className="nl-field"><span className="nl-field__label">{t("account.confirmPassword")}</span>
            <input className="nl-input" type="password" name="confirm" required minLength={8} autoComplete="new-password" />
          </label>
          <div className="feedback-card__footer">
            <Button type="submit" loading={pwState === "saving"}>{t("account.changeButton")}</Button>
            {pwState === "saved" && <p className="settings-notice">{t("account.pwChanged")}</p>}
            {pwState === "mismatch" && <p className="form-error" role="alert">{t("account.pwMismatch")}</p>}
            {pwState === "error" && <p className="form-error" role="alert">{t("account.pwWrongCurrent")}</p>}
            {pwState === "failed" && <p className="form-error" role="alert">{t("common.error")}</p>}
          </div>
        </form>
      </div>
    </Card>
  );
}

function LanguageCard({ locale, setLocale }: { locale: string; setLocale: (value: never) => void }) {
  const { t } = useI18n();
  return (
    <Card className="settings-card">
      <h2>{t("settings.language")}</h2>
      <div className="settings-grid">
        <SelectField label={t("settings.language")} value={locale} onChange={(event) => setLocale(event.target.value as never)}>
          {languages.map((language) => <option key={language.code} value={language.code}>{language.nativeLabel}</option>)}
        </SelectField>
      </div>
    </Card>
  );
}

/** Phone-notification connection toggle (owner law): flipping it ON asks the
 *  browser/OS for notification permission, subscribes Web Push, and fires a
 *  real confirmation push so "connected" is proven, not promised. */
function PushConnectionCard() {
  const { t } = useI18n();
  const [state, setState] = useState<PushConnection | null>(null);
  const [busy, setBusy] = useState(false);
  const [confirmed, setConfirmed] = useState(false);

  useEffect(() => { void pushConnection().then(setState); }, []);

  const toggle = async () => {
    setBusy(true);
    setConfirmed(false);
    try {
      if (state === "on") {
        await disableWebPush();
        setState(await pushConnection());
        return;
      }
      if (typeof Notification !== "undefined" && Notification.permission === "default") {
        await Notification.requestPermission();
      }
      await enableWebPush();
      const next = await pushConnection();
      setState(next);
      if (next === "on") {
        // proof of connection: a real push that should land on the phone now
        try { await api(`/push/test?locale=fa`, { method: "POST" }); setConfirmed(true); } catch { /* test push is best-effort */ }
      }
    } finally {
      setBusy(false);
    }
  };

  const on = state === "on";
  const blocked = state === "blocked";
  return (
    <Card className="settings-card push-connect">
      <h2>{t("push.connectTitle")}</h2>
      <p className="settings-card__lead">{t("push.connectLead")}</p>
      <div className="push-connect__row">
        <span className={`push-connect__state push-connect__state--${state ?? "unknown"}`}>
          {state === null
            ? "…"
            : on ? t("push.stateOn") : blocked ? t("push.stateBlocked") : state === "unsupported" ? t("push.stateUnsupported") : t("push.stateOff")}
        </span>
        <button
          type="button"
          role="switch"
          aria-checked={on}
          aria-label={t("push.connectTitle")}
          className={`nl-switch${on ? " is-on" : ""}`}
          disabled={busy || state === "unsupported" || state === null}
          onClick={() => void toggle()}
        >
          <span className="nl-switch__thumb" />
        </button>
      </div>
      {confirmed && <div className="settings-notice">{t("push.confirmSent")}</div>}
      {blocked && <div className="form-error">{t("push.blockedHint")}</div>}
    </Card>
  );
}

/** Push verification: fires a real Web Push to every device this account
 *  subscribed, so "is my phone actually connected" becomes a one-tap answer. */
function PushTestCard() {
  const { t, locale } = useI18n();
  const [busy, setBusy] = useState(false);
  const [result, setResult] = useState<{ total: number; sent: number; pruned: number } | null>(null);
  const [failed, setFailed] = useState(false);

  const sendTest = async () => {
    setBusy(true);
    setFailed(false);
    setResult(null);
    try {
      setResult(await api<{ total: number; sent: number; pruned: number }>(`/push/test?locale=${encodeURIComponent(locale)}`, { method: "POST" }));
    } catch {
      setFailed(true);
    } finally {
      setBusy(false);
    }
  };

  return (
    <Card className="settings-card">
      <h2>{t("push.testTitle")}</h2>
      <p className="settings-card__lead">{t("push.testLead")}</p>
      <Button icon="bell" loading={busy} onClick={() => void sendTest()}>{t("push.testButton")}</Button>
      {result && (
        <div className="settings-notice">
          {result.total === 0
            ? t("push.testNone")
            : t("push.testSent", { sent: result.sent, total: result.total })}
          {result.pruned > 0 && ` ${t("push.testPruned", { pruned: result.pruned })}`}
        </div>
      )}
      {failed && <div className="form-error">{t("push.testFail")}</div>}
    </Card>
  );
}

function FeedbackCard() {
  const { t } = useI18n();
  const [feedbackCategory, setFeedbackCategory] = useState("suggestion");
  const [feedbackMessage, setFeedbackMessage] = useState("");
  const [feedbackState, setFeedbackState] = useState<"idle" | "sending" | "sent" | "error">("idle");
  const sendFeedback = async (event: React.FormEvent) => {
    event.preventDefault();
    if (isDemoMode()) { setFeedbackState("sent"); setFeedbackMessage(""); return; }
    setFeedbackState("sending");
    try {
      await api("/feedback", { method: "POST", body: JSON.stringify({ category: feedbackCategory, message: feedbackMessage, page: "settings" }) });
      setFeedbackState("sent");
      setFeedbackMessage("");
    } catch {
      setFeedbackState("error");
    }
  };
  return (
    <Card className="settings-card feedback-card">
      <h2>{t("feedback.title")}</h2>
      <p>{t("feedback.subtitle")}</p>
      <form onSubmit={sendFeedback}>
        <SelectField label={t("feedback.category")} value={feedbackCategory} onChange={(event) => setFeedbackCategory(event.target.value)}>
          <option value="suggestion">{t("feedback.suggestion")}</option>
          <option value="complaint">{t("feedback.complaint")}</option>
          <option value="bug">{t("feedback.bug")}</option>
          <option value="other">{t("feedback.other")}</option>
        </SelectField>
        <label className="nl-field">
          <span className="nl-field__label">{t("feedback.message")}</span>
          <textarea value={feedbackMessage} onChange={(event) => setFeedbackMessage(event.target.value)} required minLength={3} maxLength={4000} placeholder={t("feedback.message")} />
        </label>
        <div className="feedback-card__footer">
          <Button type="submit" loading={feedbackState === "sending"}>{t("feedback.send")}</Button>
          {feedbackState === "sent" && <p className="settings-notice">{t("feedback.success")}</p>}
          {feedbackState === "error" && <p className="form-error" role="alert">{t("feedback.error")}</p>}
        </div>
      </form>
    </Card>
  );
}

function PrivacyCard({ onReplayTour }: { onReplayTour: () => void }) {
  const { t } = useI18n();
  const { logout } = useAuth();
  const navigate = useNavigate();
  const [notice, setNotice] = useState("");
  const [busy, setBusy] = useState<"reset" | "delete" | null>(null);
  const [popup, setPopup] = useState<"reset" | "delete" | null>(null);

  const request = async (type: string) => {
    if (!isDemoMode()) await api("/privacy-requests", { method: "POST", body: JSON.stringify({ type }) }).catch(() => undefined);
    setNotice(t("privacy.requestRegistered"));
  };

  const resetApp = async () => {
    setBusy("reset");
    try {
      await api("/auth/account/reset", { method: "POST" });
      ["nelurio.routines.v1", "nelurio.routines.done.v1", "nelurio.semesters.v1", "nelurio.categories.v1", "nelurio.tree"].forEach((key) => window.localStorage.removeItem(key));
      setPopup(null);
      window.location.href = "/";
    } catch {
      setNotice(t("common.error"));
    } finally {
      setBusy(null);
    }
  };

  const deleteAccount = async () => {
    setBusy("delete");
    try {
      await api("/auth/account", { method: "DELETE" });
      await logout();
      window.location.href = "/auth";
    } catch {
      setNotice(t("common.error"));
      setBusy(null);
    }
  };

  return (
    <>
      <Card className="settings-card">
        <h2>{t("tour.title")}</h2>
        <p>{t("more.subtitle")}</p>
        <Button variant="secondary" icon="play" onClick={onReplayTour}>{t("settings.replayTour")}</Button>
      </Card>
      <Card className="settings-card">
        <h2>{t("privacy.calendarTitle")}</h2>
        <p className="settings-card__lead">{t("privacy.calendarLead")}</p>
        <div className="privacy-actions">
          <a className="ics-link" href={`${API_BASE_URL}/calendar-feed.ics`} target="_blank" rel="noreferrer">{t("privacy.calendarOpen")}</a>
        </div>
      </Card>
      <Card className="settings-card">
        <h2>{t("settings.privacy")}</h2>
        <div className="privacy-actions">
          <button onClick={() => void request("export")}>{t("privacy.export")}</button>
          <button onClick={() => void request("correction")}>{t("privacy.consent")}</button>
        </div>
        {notice && <p className="settings-notice">{notice}</p>}
      </Card>
      <Card className="settings-card danger-zone">
        <h2>{t("privacy.dangerTitle")}</h2>
        <p className="settings-card__lead">{t("privacy.resetLead")}</p>
        <div className="privacy-danger-actions">
          <Button variant="secondary" icon="trash" onClick={() => setPopup("reset")}>{t("privacy.resetButton")}</Button>
          <Button variant="danger" icon="warning" onClick={() => setPopup("delete")}>{t("privacy.deleteButton")}</Button>
        </div>
      </Card>
      {popup && (
        <div className="composer-backdrop" onClick={() => setPopup(null)} role="presentation">
          <div className="composer-modal rise" role="alertdialog" aria-modal="true" onClick={(event) => event.stopPropagation()}>
            <div className="composer-modal__head">
              <h2>{popup === "reset" ? t("privacy.resetConfirmTitle") : t("privacy.deleteConfirmTitle")}</h2>
              <button className="composer-modal__close" onClick={() => setPopup(null)} aria-label={t("common.cancel")}><Icon name="x" size={16} /></button>
            </div>
            <p className="danger-popup-text">{popup === "reset" ? t("privacy.resetConfirmBody") : t("privacy.deleteConfirmBody")}</p>
            <div className="composer-modal__actions">
              <Button type="button" variant="secondary" onClick={() => setPopup(null)}>{t("common.cancel")}</Button>
              <Button type="button" variant="danger" icon="warning" loading={busy !== null} onClick={() => void (popup === "reset" ? resetApp() : deleteAccount())}>
                {popup === "reset" ? t("privacy.resetConfirmButton") : t("privacy.deleteConfirmButton")}
              </Button>
            </div>
          </div>
        </div>
      )}
    </>
  );
}


function AppearanceCard() {
  const { t } = useI18n();
  const [accent, setAccent] = useState<AccentKey>(() => readAccent());
  // The editor is the DASHBOARD itself (iOS jiggle): this button just navigates.
  const [layout, setLayout] = useState(() => loadLayout());
  const navigate = useNavigate();
  useEffect(() => {
    const reload = () => setLayout(loadLayout());
    window.addEventListener("nelurio:dashboard-layout", reload);
    return () => window.removeEventListener("nelurio:dashboard-layout", reload);
  }, []);
  const choose = (key: AccentKey) => {
    setAccent(key);
    storeAccent(key);
    applyAccent(key);
  };
  return (
    <>
      <Card className="settings-card">
        <h2>{t("dbEdit.title")}</h2>
        <p className="settings-card__lead">{t("dbEdit.settingsLead")}</p>
        <div className="dash-edit-row">
          <Badge tone="purple">{t("dbEdit.count", { count: layout.items.length })}</Badge>
          <div className="dash-edit-row__buttons">
            <Button onClick={() => { const fresh = defaultLayout(); saveLayout(fresh); setLayout(fresh); notifyLayoutChanged(); }}>{t("dbEdit.reset")}</Button>
            <Button icon="settings" onClick={() => navigate("/?edit=1")}>{t("dbEdit.open")}</Button>
          </div>
        </div>
      </Card>
      <Card className="settings-card">
        <h2>{t("theme.title")}</h2>
      <p className="settings-card__lead">{t("theme.lead")}</p>
      <div className="accent-grid" role="radiogroup" aria-label={t("theme.title")}>
        {ACCENTS.map((palette) => (
          <button
            key={palette.key}
            role="radio"
            aria-checked={accent === palette.key}
            className={`accent-option ${accent === palette.key ? "is-selected" : ""}`}
            onClick={() => choose(palette.key)}
          >
            <span className="accent-option__swatch" style={{ background: `linear-gradient(140deg, ${palette.swatch}, color-mix(in srgb, ${palette.swatch} 55%, #1c1450))` }} />
            <strong>{t(palette.labelKey)}</strong>
            {accent === palette.key && <Badge tone="purple">{t("theme.active")}</Badge>}
          </button>
        ))}
      </div>
      <p className="tree-picker__hint">{t("theme.hint")}</p>
    </Card>
    </>
  );
}

// ---- Subscription (premium foundation) ----
// Product logic: the Nelurio app is ONE app of the Nelurio platform. In-app we
// show only this app's trial/premium state; buying unlimited happens on the
// platform website — the CTA is a plain external handover, no fake checkout.

// The platform website (owner's domain). Overridable per environment.
const PLATFORM_URL = "https://nelurio.com";

const APP_META: Array<{ slug: string; nameKey: string; descKey: string }> = [
  { slug: "nelurio", nameKey: "subscription.app.nelurio", descKey: "subscription.desc.nelurio" },
  { slug: "finello", nameKey: "subscription.app.finello", descKey: "subscription.desc.finello" },
  { slug: "nexdeutsch", nameKey: "subscription.app.nexdeutsch", descKey: "subscription.desc.nexdeutsch" },
  { slug: "washhalle", nameKey: "subscription.app.washhalle", descKey: "subscription.desc.washhalle" }
];


function SubscriptionCard() {
  const { t, formatDate } = useI18n();
  // Hooks sit above every conditional render (repo trap #3).
  const [apps, setApps] = useState<Entitlement[] | null>(null);
  const [busySlug, setBusySlug] = useState<string | null>(null);
  const [notice, setNotice] = useState<"started" | "checkoutSoon" | "error" | null>(null);

  useEffect(() => {
    let cancelled = false;
    if (isDemoMode()) {
      setApps(APP_META.map((app) => ({ appSlug: app.slug, status: "available", daysLeft: 0 })));
      return () => { cancelled = true; };
    }
    api<{ apps: Entitlement[] }>("/entitlements")
      .then((data) => { if (!cancelled) setApps(data.apps); })
      .catch(() => { if (!cancelled) { setApps([]); setNotice("error"); } });
    return () => { cancelled = true; };
  }, []);

  const startTrial = async (slug: string) => {
    if (isDemoMode()) {
      setApps((current) => (current ?? []).map((app) => (app.appSlug === slug ? { ...app, status: "trial", daysLeft: 30 } : app)));
      setNotice("started");
      return;
    }
    setBusySlug(slug);
    try {
      // Idempotent server-side: a second call returns the current state.
      const updated = await api<Entitlement>(`/entitlements/${slug}/trial`, { method: "POST" });
      setApps((current) => (current ?? []).map((app) => (app.appSlug === slug ? updated : app)));
      setNotice("started");
    } catch {
      setNotice("error");
    } finally {
      setBusySlug(null);
    }
  };

  const stateBadge = (app: Entitlement) => {
    switch (app.status) {
      case "trial":
        return <Badge tone="blue">{t("subscription.status.trial", { days: app.daysLeft })}</Badge>;
      case "premium":
        return <Badge tone="purple" icon="star">{t("subscription.status.premium", { date: app.premiumUntil ? formatDate(app.premiumUntil) : "—" })}</Badge>;
      case "expired":
        return <Badge tone="amber">{t("subscription.status.expired")}</Badge>;
      default:
        return <Badge tone="neutral">{t("subscription.status.available")}</Badge>;
    }
  };

  return (
    <Card className="settings-card subscription-card">
      <h2>{t("subscription.title")}</h2>
      <p className="settings-card__lead">{t("subscription.lead")}</p>
      {apps === null && (
        <div className="subscription-rows">
          {APP_META.map((meta) => (
            <div className="subscription-row" key={meta.slug}>
              <div className="subscription-row__info">
                <strong>{t(meta.nameKey)}</strong>
                <span>{t(meta.descKey)}</span>
              </div>
              <Skeleton width={140} height={22} />
            </div>
          ))}
        </div>
      )}
      {apps !== null && (() => {
        // Product logic: THIS app (Nelurio) shows only its own trial/premium
        // state. The platform-wide matrix and checkout live on the Nelurio
        // platform website — the CTA hands over there.
        const app = apps.find((entry) => entry.appSlug === "nelurio") ?? { appSlug: "nelurio", status: "available" as const, daysLeft: 0 };
        return (
          <div className="subscription-rows">
            <div className="subscription-row">
              <div className="subscription-row__info">
                <strong>{t("subscription.app.nelurio")}</strong>
                <span>{t("subscription.desc.nelurio")}</span>
              </div>
              <div className="subscription-row__state">
                {stateBadge(app)}
                {app.status === "available" && (
                  <Button variant="secondary" icon="play" loading={busySlug === "nelurio"} onClick={() => void startTrial("nelurio")}>
                    {t("subscription.startTrial")}
                  </Button>
                )}
              </div>
            </div>
            <a className="subscription-cta" href={PLATFORM_URL} target="_blank" rel="noreferrer">
              <Icon name="star" size={16} />
              <span>{app.status === "premium" ? t("subscription.manageCta") : t("subscription.buyCta")}</span>
              <small>{t("subscription.ctaHint")}</small>
            </a>
          </div>
        );
      })()}
      {notice === "started" && <p className="settings-notice">{t("subscription.trialStarted")}</p>}
      {notice === "error" && <p className="form-error" role="alert">{t("common.error")}</p>}
    </Card>
  );
}
